import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  LogOut, UserCircle, Sparkles, HelpCircle,
  Settings as SettingsIcon, Send, Bug, Lightbulb, ArrowLeftRight
} from 'lucide-react';
import { useAuth } from '../../../lib/auth';
import { useActiveRoleStore } from '../../../lib/stores/activeRoleStore';
import { usePermissions } from '../../../lib/hooks';

interface HeaderUserMenuProps {
  setShowProfileModal: (open: boolean) => void;
  setShowChangelogModal: (open: boolean) => void;
  setShowGuideModal: (open: boolean) => void;
  onFeedback: (type: 'bug' | 'feature') => void;
  onSignOut: () => void;
}

export function HeaderUserMenu({
  setShowProfileModal,
  setShowChangelogModal,
  setShowGuideModal,
  onFeedback,
  onSignOut,
}: HeaderUserMenuProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const { user, isSuperAdmin, rawIsSuperAdmin, rawIsPartnerAdmin, userProfile } = useAuth();
  const { hasResourceAccess, hasPermission } = usePermissions();
  const { activeRole } = useActiveRoleStore();
  const isDualRole = !!userProfile?.organization_id && (rawIsSuperAdmin || rawIsPartnerAdmin);

  const updatePos = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setMenuPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
  };

  useEffect(() => {
    if (!showMenu) return;
    updatePos();
    const onResize = () => updatePos();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [showMenu]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !portalRef.current?.contains(target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const userImage = userProfile?.avatar_url || userProfile?.profile_image_url;
  const userInitial = (
    userProfile?.full_name?.[0] ||
    user?.user_metadata?.full_name?.[0] ||
    user?.email?.[0] ||
    'U'
  ).toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        aria-label="Profilo"
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center space-x-2 text-sm hover:opacity-80 transition-opacity"
      >
        <div
          className="h-7 w-7 rounded-full flex items-center justify-center overflow-hidden"
          style={userImage ? undefined : { background: 'linear-gradient(135deg, rgba(233,30,140,0.7), rgba(249,115,22,0.7))' }}
        >
          {userImage ? (
            <img src={userImage} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <span className="font-semibold text-xs text-white">{userInitial}</span>
          )}
        </div>
      </button>

      {showMenu && createPortal(
        <div
          ref={portalRef}
          className="fixed w-56 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-xl py-1 border z-[100] max-h-[calc(100vh-1rem)] overflow-y-auto"
          style={{ borderColor: 'var(--theme-border)', top: menuPos.top, right: menuPos.right }}
        >
          <button
            onClick={() => { setShowMenu(false); setShowChangelogModal(true); }}
            className="flex items-center w-full px-4 py-3 text-sm text-foreground hover:bg-accent border-b"
            style={{ borderColor: 'var(--theme-border)' }}
          >
            <Sparkles className="h-4 w-4 mr-3 text-blue-600" />
            <div className="flex-1 text-left">
              <div className="font-medium">Versione 3.3.0</div>
              <div className="text-xs text-muted-foreground">Novità e aggiornamenti</div>
            </div>
          </button>

          <button
            onClick={() => { setShowMenu(false); setShowGuideModal(true); }}
            className="flex items-center w-full px-4 py-3 text-sm text-foreground hover:bg-accent border-b"
            style={{ borderColor: 'var(--theme-border)' }}
          >
            <HelpCircle className="h-4 w-4 mr-3 text-green-600" />
            <div className="flex-1 text-left">
              <div className="font-medium">Guida</div>
              <div className="text-xs text-muted-foreground">Assistenza completa</div>
            </div>
          </button>

          <button
            onClick={() => { setShowMenu(false); setShowProfileModal(true); }}
            className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-accent"
          >
            <UserCircle className="h-4 w-4 mr-3" />
            Modifica profilo
          </button>

          {hasPermission('email_signature', 'generate') && (
            <Link
              to="/signature-generator"
              onClick={() => setShowMenu(false)}
              className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent"
            >
              <Send className="h-4 w-4 mr-3" />
              Genera firma email
            </Link>
          )}

          {!isSuperAdmin && hasResourceAccess('settings') && (
            <Link
              to="/settings"
              onClick={() => setShowMenu(false)}
              className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent"
            >
              <SettingsIcon className="h-4 w-4 mr-3" />
              Impostazioni
            </Link>
          )}

          <button
            onClick={() => { setShowMenu(false); onFeedback('bug'); }}
            className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-accent"
          >
            <Bug className="h-4 w-4 mr-3 text-orange-500" />
            Segnala un bug
          </button>

          <button
            onClick={() => { setShowMenu(false); onFeedback('feature'); }}
            className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-accent"
          >
            <Lightbulb className="h-4 w-4 mr-3 text-purple-500" />
            Richiedi funzionalità
          </button>

          {isDualRole && (
            <>
              <div className="border-t mt-1" style={{ borderColor: 'var(--theme-border)' }} />
              <button
                onClick={() => {
                  const newRole = activeRole === 'admin' ? 'crm' : 'admin';
                  const overlay = document.createElement('div');
                  overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.7);backdrop-filter:blur(4px);';
                  overlay.innerHTML = `<div style="text-align:center;color:white;"><div style="width:32px;height:32px;border:3px solid rgba(255,255,255,0.3);border-top-color:white;border-radius:50%;animation:spin 0.6s linear infinite;margin:0 auto 12px;"></div><div style="font-size:14px;font-weight:500;">${newRole === 'admin' ? 'Accesso Area Admin...' : 'Accesso al CRM...'}</div></div><style>@keyframes spin{to{transform:rotate(360deg)}}</style>`;
                  document.body.appendChild(overlay);
                  localStorage.setItem('flusia_active_role', newRole);
                  setTimeout(() => {
                    window.location.href = newRole === 'crm'
                      ? '/dashboard'
                      : (rawIsSuperAdmin ? '/organizations' : '/partner-admin');
                  }, 150);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-accent"
              >
                <ArrowLeftRight className="h-4 w-4 mr-3 text-blue-500" />
                {activeRole === 'admin' ? 'Vai al CRM' : "Vai all'Area Admin"}
              </button>
            </>
          )}

          <div className="border-t mt-1" style={{ borderColor: 'var(--theme-border)' }} />
          <button
            onClick={() => { setShowMenu(false); onSignOut(); }}
            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4 mr-3" />
            Disconnetti
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
